import React, { useCallback, useMemo, useState } from 'react';
import { TeamInfo } from '../TeamInfo';
import { ActiveEvent, PlannedDriver, RacePlan, RaceRules } from '../types';
import {
  checkCompliance,
  DriverBounds,
  DriverCompliance,
  driverTimes,
  nextDriver,
  normalizeDriverLabel,
} from '../analytics/driveTime';
import { saveRacePlan } from '../auth/api';
import './DriverPlanPanel.css';

interface DriverPlanPanelProps {
  teams: Map<string, TeamInfo>;
  /** Highlighted teams — the ones a plan is kept for. */
  ourTeamIds: Set<string>;
  rules: RaceRules;
  minutesRemaining: number | null;
  plan: RacePlan;
  /** Null when replaying or when nothing is being recorded; disables editing. */
  activeEvent: ActiveEvent | null;
  readOnly?: boolean;
}

function formatMinutes(minutes: number): string {
  const whole = Math.round(minutes);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
}

const STATUS_TEXT: Record<DriverCompliance['status'], string> = {
  ok: '',
  short: 'still owes',
  'at-risk': 'cannot reach minimum',
  'approaching-max': 'near max',
  'over-max': 'over max',
};

function emptyDriver(name: string): PlannedDriver {
  return { name, targetStintMinutes: null, minMinutes: null, maxMinutes: null };
}

const TeamBlock: React.FC<{
  team: TeamInfo;
  rules: RaceRules;
  minutesRemaining: number | null;
  planned: PlannedDriver[];
  readOnly: boolean;
  onChange: (drivers: PlannedDriver[]) => void;
}> = ({ team, rules, minutesRemaining, planned, readOnly, onChange }) => {
  const [newName, setNewName] = useState('');

  const overrides = useMemo(() => {
    const map = new Map<string, DriverBounds>();
    for (const d of planned) {
      if (d.minMinutes !== null || d.maxMinutes !== null) {
        map.set(normalizeDriverLabel(d.name), {
          minMinutes: d.minMinutes,
          maxMinutes: d.maxMinutes,
        });
      }
    }
    return map;
  }, [planned]);

  const compliance = useMemo(
    () => checkCompliance(driverTimes(team), rules, minutesRemaining, overrides),
    [team, rules, minutesRemaining, overrides],
  );

  /**
   * Everyone the plan names, plus anyone who has actually driven but was never
   * planned — an unplanned driver still accrues time against the regulations,
   * so leaving them off the board would hide a breach.
   */
  const rows = useMemo(() => {
    const byName = new Map(compliance.drivers.map((d) => [d.driver.toLowerCase(), d]));
    const seen = new Set<string>();

    const ordered = planned.map((p) => {
      const key = normalizeDriverLabel(p.name).toLowerCase();
      seen.add(key);
      return { planned: p, driven: byName.get(key) ?? null };
    });

    const unplanned = compliance.drivers
      .filter((d) => !seen.has(d.driver.toLowerCase()))
      .map((d) => ({ planned: null, driven: d }));

    return [...ordered, ...unplanned];
  }, [planned, compliance]);

  const upNext = nextDriver(planned.map((p) => p.name), team.driver);

  const addDriver = (): void => {
    const name = newName.trim();
    if (!name) return;
    if (planned.some((p) => p.name.toLowerCase() === name.toLowerCase())) return;
    onChange([...planned, emptyDriver(name)]);
    setNewName('');
  };

  const move = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= planned.length) return;
    const next = [...planned];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <section className="dp-team">
      <header className="dp-team-head">
        <span className="dp-kart">#{team.kartNumber || '—'}</span>
        <span className="dp-team-name">{team.teamName || team.driver || 'Unnamed'}</span>
        {upNext && (
          <span className="dp-next">
            Up next: <b>{upNext}</b>
          </span>
        )}
      </header>

      {!compliance.feasible && (
        <div className="dp-alarm">
          The minimums no longer fit: {formatMinutes(compliance.totalShortfallMinutes)}{' '}
          still owed with {minutesRemaining === null ? '—' : formatMinutes(minutesRemaining)}{' '}
          of the race left. Only one driver can be in the kart at a time.
        </div>
      )}

      <table className="dp-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Driver</th>
            <th className="dp-num">Driven</th>
            <th className="dp-num">Laps</th>
            <th className="dp-num">Target</th>
            <th>Status</th>
            {!readOnly && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={readOnly ? 6 : 7} className="dp-empty">
                No drivers planned yet.
              </td>
            </tr>
          )}
          {rows.map(({ planned: p, driven }, index) => {
            const status = driven?.status ?? 'ok';
            const name = p?.name ?? driven?.driver ?? '';
            return (
              <tr
                key={name || index}
                className={`dp-row dp-row--${status}${driven?.isCurrent ? ' dp-row--current' : ''}`}
              >
                <td className="dp-num">{p ? index + 1 : '–'}</td>
                <td>
                  {name}
                  {driven?.isCurrent && <span className="dp-out"> · out now</span>}
                  {!p && <span className="dp-unplanned"> · unplanned</span>}
                </td>
                <td className="dp-num">
                  {driven ? formatMinutes(driven.minutes) : '—'}
                </td>
                <td className="dp-num">{driven?.lapCount ?? 0}</td>
                <td className="dp-num">
                  {p?.targetStintMinutes ? `${p.targetStintMinutes}m` : '—'}
                </td>
                <td className="dp-status">
                  {status === 'ok'
                    ? '✓'
                    : `${STATUS_TEXT[status]}${
                        status === 'short' || status === 'at-risk'
                          ? ` ${formatMinutes(driven?.shortfallMinutes ?? 0)}`
                          : status === 'over-max'
                            ? ` ${formatMinutes(Math.abs(driven?.headroomMinutes ?? 0))}`
                            : driven?.headroomMinutes !== null &&
                                driven?.headroomMinutes !== undefined
                              ? ` (${formatMinutes(driven.headroomMinutes)} left)`
                              : ''
                      }`}
                </td>
                {!readOnly && (
                  <td className="dp-actions">
                    {p && (
                      <>
                        <button type="button" onClick={() => move(index, -1)} aria-label={`Move ${name} up`}>
                          ↑
                        </button>
                        <button type="button" onClick={() => move(index, 1)} aria-label={`Move ${name} down`}>
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => onChange(planned.filter((_, i) => i !== index))}
                          aria-label={`Remove ${name}`}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {!readOnly && (
        <div className="dp-add">
          <input
            type="text"
            value={newName}
            placeholder="Add driver…"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addDriver()}
            aria-label={`Add a driver to kart ${team.kartNumber}`}
          />
          <button type="button" onClick={addDriver} disabled={!newName.trim()}>
            Add
          </button>
        </div>
      )}
    </section>
  );
};

export const DriverPlanPanel: React.FC<DriverPlanPanelProps> = ({
  teams,
  ourTeamIds,
  rules,
  minutesRemaining,
  plan,
  activeEvent,
  readOnly = false,
}) => {
  const [draft, setDraft] = useState<RacePlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server state until edited, then the local draft until saved.
  const working = draft ?? plan;
  const editable = !readOnly && activeEvent !== null;

  const ourTeams = useMemo(
    () => Array.from(teams.values()).filter((t) => ourTeamIds.has(t.id)),
    [teams, ourTeamIds],
  );

  const driversFor = useCallback(
    (teamId: string): PlannedDriver[] =>
      working.teams.find((t) => t.teamId === teamId)?.drivers ?? [],
    [working],
  );

  const updateTeam = useCallback(
    (teamId: string, drivers: PlannedDriver[]) => {
      setDraft((prev) => {
        const base = prev ?? plan;
        const others = base.teams.filter((t) => t.teamId !== teamId);
        return { teams: [...others, { teamId, drivers }] };
      });
    },
    [plan],
  );

  const save = useCallback(async () => {
    if (!activeEvent || !draft) return;
    setSaving(true);
    setError(null);
    try {
      await saveRacePlan(activeEvent.id, draft);
      // The server echoes the plan back over the socket, so drop the draft and
      // follow that rather than keeping a second copy in sync by hand.
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the plan');
    } finally {
      setSaving(false);
    }
  }, [activeEvent, draft]);

  return (
    <div className="dp-panel">
      <div className="dp-header">
        <h3>Driver plan</h3>
        {rules.minDriverMinutes !== null || rules.maxDriverMinutes !== null ? (
          <span className="dp-rules">
            {rules.minDriverMinutes !== null && `min ${rules.minDriverMinutes}m`}
            {rules.minDriverMinutes !== null && rules.maxDriverMinutes !== null && ' · '}
            {rules.maxDriverMinutes !== null && `max ${rules.maxDriverMinutes}m`}
            {' per driver'}
          </span>
        ) : (
          <span className="dp-rules dp-muted">
            No drive-time limits set for this event
          </span>
        )}
        {editable && draft && (
          <span className="dp-save">
            <button type="button" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save plan'}
            </button>
            <button type="button" onClick={() => setDraft(null)} disabled={saving}>
              Discard
            </button>
          </span>
        )}
      </div>

      {error && <div className="dp-error">{error}</div>}

      {!readOnly && activeEvent === null && (
        <div className="dp-note">
          Editing needs a race being recorded — start one to save a plan.
        </div>
      )}

      {ourTeams.length === 0 ? (
        <div className="dp-empty">
          Click a row in the grid to mark a kart as yours, then plan its running
          order here.
        </div>
      ) : (
        ourTeams.map((team) => (
          <TeamBlock
            key={team.id}
            team={team}
            rules={rules}
            minutesRemaining={minutesRemaining}
            planned={driversFor(team.id)}
            readOnly={!editable}
            onChange={(drivers) => updateTeam(team.id, drivers)}
          />
        ))
      )}
    </div>
  );
};

export default DriverPlanPanel;
